import React, { useState, useEffect } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    Button, TextField, FormControlLabel, Checkbox, Box, Stack, FormHelperText,
    Typography, List, ListItem, ListItemButton, ListItemIcon, ListItemText,
} from '@mui/material';
import { Shift } from '../../interfaces/shift/Shift';
import { BulkAssignmentRequest } from '../../interfaces/shift/ShiftAssignment';
import { ShiftAssignmentService } from '../../services/shift-assignment.service';
import { useToast } from '../../context/ToastContext';
import { displayUsername } from '../../utils/userUtils';

import SearchableSelect from '../common/SearchableSelect';
import { toLocalIsoDate } from '../../utils/dateUtils';
import { helpWhy } from '../../help/why';
interface SimpleUser {
    id: string;
    username: string;
    name?: string;
}

interface Props {
    open: boolean;
    onClose: () => void;
    institutionId: string;
    shifts: Shift[];
    users: SimpleUser[];
    onSaved: () => void;
}

const USER_CAP = 1000;
const today = () => toLocalIsoDate();

const BulkAssignmentDialog: React.FC<Props> = ({ open, onClose, institutionId, shifts, users, onSaved }) => {
    const toast = useToast();
    const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
    const [shiftId, setShiftId] = useState('');
    const [effectiveFrom, setEffectiveFrom] = useState(today());
    const [effectiveTo, setEffectiveTo] = useState('');
    const [openEnded, setOpenEnded] = useState(true);
    const [replaceExisting, setReplaceExisting] = useState(true);
    const [saving, setSaving] = useState(false);
    const [errors, setErrors] = useState<{ shiftId?: string; effectiveFrom?: string; effectiveTo?: string; users?: string }>({});

    useEffect(() => {
        if (open) {
            setSelectedIds(new Set());
            setShiftId('');
            setEffectiveFrom(today());
            setEffectiveTo('');
            setOpenEnded(true);
            setReplaceExisting(false);
            setErrors({});
        }
    }, [open]);

    const toggleUser = (id: string) => {
        setSelectedIds(prev => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
        setErrors(prev => ({ ...prev, users: undefined }));
    };

    const selectAll = () => {
        const ids = users.slice(0, USER_CAP).map(u => u.id);
        setSelectedIds(new Set(ids));
        setErrors(prev => ({ ...prev, users: undefined }));
    };

    const clearAll = () => {
        setSelectedIds(new Set());
    };

    const validate = (): boolean => {
        const errs: typeof errors = {};
        if (selectedIds.size === 0) errs.users = 'Select at least one user';
        if (selectedIds.size > USER_CAP) errs.users = `Maximum ${USER_CAP} users allowed`;
        if (!shiftId) errs.shiftId = 'Shift is required';
        if (!effectiveFrom) errs.effectiveFrom = 'Effective from is required';
        if (!openEnded && effectiveTo && effectiveTo < effectiveFrom) {
            errs.effectiveTo = 'Must be on or after effective from';
        }
        setErrors(errs);
        return Object.keys(errs).length === 0;
    };

    const handleSubmit = async () => {
        if (!validate()) return;
        setSaving(true);
        try {
            const body: BulkAssignmentRequest = {
                userIds: Array.from(selectedIds),
                shiftId,
                effectiveFrom,
                effectiveTo: openEnded ? null : (effectiveTo || null),
                replaceExisting,
            };
            const result = await ShiftAssignmentService.bulkCreate(institutionId, body);
            toast.showSuccess(`Bulk assignment saved for ${result.length} user(s)`);
            onSaved();
            onClose();
        } catch (err) {
            toast.showApiError(err, 'Failed to bulk assign');
        } finally {
            setSaving(false);
        }
    };

    const activeShifts = shifts.filter(s => s.isActive);
    const submitDisabled = saving || selectedIds.size === 0 || selectedIds.size > USER_CAP;
    const submitWhy = saving ? 'The assignment is being saved.' : selectedIds.size === 0 ? 'Tick at least one person first.' : selectedIds.size > USER_CAP ? `Too many people ticked; the limit is ${USER_CAP}.` : null;

    return (
        <Dialog open={open} onClose={onClose} PaperProps={{ 'data-help-page': 'shifts.bulkAssign.page' } as any} maxWidth="md" fullWidth>
            <DialogTitle>Bulk Assign Shift</DialogTitle>
            <DialogContent>
                <Stack data-help-section="shifts.bulkAssign.section.details" spacing={2} sx={{ pt: 1 }}>
                    {/* User selection */}
                    <Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, flexWrap: 'wrap', gap: 1 }}>
                            <Typography variant="subtitle2">
                                Users ({selectedIds.size} selected)
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 1 }}>
                                <Button size="small" data-help="shifts.bulkAssign.selectAll" onClick={selectAll} data-testid="bulk-select-all">Select all visible</Button>
                                <Button size="small" data-help="shifts.bulkAssign.clear" onClick={clearAll} data-testid="bulk-clear">Clear</Button>
                            </Box>
                        </Box>
                        {errors.users && <FormHelperText error>{errors.users}</FormHelperText>}
                        <Box data-help="shifts.bulkAssign.field.users" sx={{ maxHeight: 220, overflowY: 'auto', border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                            <List dense disablePadding>
                                {users.map(u => (
                                    <ListItem key={u.id} disablePadding>
                                        <ListItemButton
                                            onClick={() => toggleUser(u.id)}
                                            data-testid={`bulk-user-${u.id}`}
                                        >
                                            <ListItemIcon sx={{ minWidth: 36 }}>
                                                <Checkbox
                                                    edge="start"
                                                    checked={selectedIds.has(u.id)}
                                                    tabIndex={-1}
                                                    disableRipple
                                                    size="small"
                                                />
                                            </ListItemIcon>
                                            <ListItemText primary={u.name || displayUsername(u.username)} />
                                        </ListItemButton>
                                    </ListItem>
                                ))}
                            </List>
                        </Box>
                        {selectedIds.size > USER_CAP && (
                            <FormHelperText error>Maximum {USER_CAP} users allowed</FormHelperText>
                        )}
                    </Box>

                    {/* Shift */}
                    <SearchableSelect
                        options={activeShifts}
                        value={shiftId}
                        onChange={id => { setShiftId(id); setErrors(prev => ({ ...prev, shiftId: undefined })); }}
                        getOptionLabel={s => `${s.name} (${s.code})`}
                        data-help="shifts.bulkAssign.field.shift" label="Shift"
                        required
                        error={!!errors.shiftId}
                        helperText={errors.shiftId || undefined}
                        data-testid="bulk-shift-select"
                    />

                    <TextField
                        data-help="shifts.bulkAssign.field.effectiveFrom" label="Effective From"
                        type="date"
                        required
                        fullWidth
                        value={effectiveFrom}
                        onChange={e => { setEffectiveFrom(e.target.value); setErrors(prev => ({ ...prev, effectiveFrom: undefined })); }}
                        error={!!errors.effectiveFrom}
                        helperText={errors.effectiveFrom}
                        InputLabelProps={{ shrink: true }}
                        inputProps={{ 'data-testid': 'bulk-effective-from' }}
                    />

                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={openEnded}
                                onChange={e => setOpenEnded(e.target.checked)}
                                data-testid="bulk-open-ended"
                            />
                        }
                        data-help="shifts.bulkAssign.field.openEnded" label="Open-ended (no end date)"
                    />

                    {!openEnded && (
                        <TextField
                            data-help="shifts.bulkAssign.field.effectiveTo" label="Effective To"
                            type="date"
                            fullWidth
                            value={effectiveTo}
                            onChange={e => { setEffectiveTo(e.target.value); setErrors(prev => ({ ...prev, effectiveTo: undefined })); }}
                            error={!!errors.effectiveTo}
                            helperText={errors.effectiveTo}
                            InputLabelProps={{ shrink: true }}
                            inputProps={{ 'data-testid': 'bulk-effective-to' }}
                        />
                    )}

                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={replaceExisting}
                                onChange={e => setReplaceExisting(e.target.checked)}
                                data-testid="bulk-replace-existing"
                            />
                        }
                        data-help="shifts.bulkAssign.field.replace" label="End-date prior assignments (replace existing) — will affect selected users"
                    />
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button data-help="shifts.bulkAssign.cancel" onClick={onClose} disabled={saving}>Cancel</Button>
                <Button
                    onClick={handleSubmit}
                    variant="contained"
                    disabled={submitDisabled}
                    {...helpWhy(submitWhy ? { kind: 'status', message: submitWhy } : null)}
                    data-help="shifts.bulkAssign.save" data-testid="bulk-save-btn"
                >
                    {saving ? 'Saving...' : `Assign (${selectedIds.size})`}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default BulkAssignmentDialog;
